import {Badge} from "@/components/ui/badge";
import {ResidentRedactedLabel} from "@/src/modules/core/components/resident-redacted-label";
import {resolveTimezone} from "@/src/modules/core/lib";
import type {ResidentMembership} from "@/src/modules/core/types/resident-membership";
import {usePropertyTypes} from "@/src/modules/properties/hooks/use-property-types";
import {useStoredCompany} from "@/src/modules/company/store";
import {SafelistingCoverageStatusCluster} from "@/src/modules/safelistings/components/safelisting-coverage-status-cluster";
import useSafelistingRangeHours from "@/src/modules/safelistings/hooks/use-safelisting-range-hours";
import {
  computeSafelistingCoverageFromDays,
  getSafelistingCoverageDateWord,
} from "@/src/modules/safelistings/lib/compute-safelisting-coverage-from-days";
import type {Safelisting} from "@/src/modules/safelistings/types";
import {formatVehicleMakeModelColorState} from "@/src/modules/vehicles/lib/format-vehicle-make-model-color-state";
import {cn} from "@/lib/utils";

type ResidentHomeSafelistingsPreviewRowProps = {
  safelisting: Safelisting;
  membership?: ResidentMembership | null;
  onSelect: (id: number) => void;
  className?: string;
  amenityRestricted?: boolean;
};

export function ResidentHomeSafelistingsPreviewRow({
  safelisting,
  membership,
  onSelect,
  className,
  amenityRestricted,
}: ResidentHomeSafelistingsPreviewRowProps) {
  const company = useStoredCompany();
  const timeZone = resolveTimezone(company?.timezone);
  const rangeHours = useSafelistingRangeHours();
  const {propertyTypes} = usePropertyTypes();

  const plate = safelisting.vehicle?.plate;
  const vehicleMeta = formatVehicleMakeModelColorState(safelisting.vehicle);

  const sortedDays = [...(safelisting.days ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  const dayCount = sortedDays.length;
  const daysLabel = dayCount === 1 ? "1 day" : dayCount > 0 ? `${dayCount} days` : null;

  const coverageDateWord = getSafelistingCoverageDateWord(safelisting.property, propertyTypes);
  const coverage = computeSafelistingCoverageFromDays(
    safelisting.days,
    timeZone,
    coverageDateWord,
    rangeHours,
  );

  return (
    <button
      type="button"
      onClick={() => onSelect(safelisting.safelisting_id)}
      className={cn(
        "hover:bg-muted/50 flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="min-w-0 truncate text-sm font-medium tabular-nums">
          <ResidentRedactedLabel value={plate} membership={membership} className="text-foreground" />
          {vehicleMeta ? (
            <span className="text-muted-foreground ml-1.5 text-xs font-normal">
              ({vehicleMeta})
            </span>
          ) : null}
        </p>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {daysLabel ? (
            <Badge variant="outline" className="shrink-0 font-normal tabular-nums">
              {daysLabel}
            </Badge>
          ) : null}
          {coverage?.overallRangeLabel ? (
            <span className="text-muted-foreground min-w-0 truncate text-xs leading-snug tabular-nums">
              {coverage.overallRangeLabel}
            </span>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
        </div>
      </div>

      <SafelistingCoverageStatusCluster
        safelisting={safelisting}
        timeZone={timeZone}
        startHumanLabel={coverage?.startHumanLabel}
        align="end"
        badgeClassName="text-xs font-medium"
        amenityRestricted={amenityRestricted}
      />
    </button>
  );
}
